import type {
  InformationRequestAudience,
  InformationRequestKind,
  InformationRequestStatus,
  InformationRequestView,
} from "@tex/finance-shared";
import { type FormEvent, useState } from "react";
import { errorMessage } from "../lib/api";
import { useDocuments } from "../lib/documents";
import { formatDate, formatDay, humanize } from "../lib/format";
import {
  type CloseCommand,
  useCloseInformationRequest,
  useCreateInformationRequest,
  useInformationRequests,
  useRespondToInformationRequest,
} from "../lib/reviews";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Mono,
  Select,
  Textarea,
} from "./ui";

const STATUS_TONE: Record<InformationRequestStatus, string> = {
  open: "bg-sky-100 text-sky-900",
  answered: "bg-violet-100 text-violet-900",
  accepted: "bg-green-100 text-green-900",
  cancelled: "bg-neutral-100 text-neutral-500",
};

function StatusBadge({ status }: { status: InformationRequestStatus }) {
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${STATUS_TONE[status]}`}
    >
      {humanize(status)}
    </span>
  );
}

function RequestSummary({
  r,
  showAudience,
}: {
  r: InformationRequestView;
  showAudience: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{r.title}</span>
        <StatusBadge status={r.status} />
        <span className="text-xs text-neutral-500">
          {humanize(r.kind)}
          {r.document_type && <> · {humanize(r.document_type)}</>}
          {showAudience && <> · to {r.audience}</>}
          {r.due_on && <> · due {formatDay(r.due_on)}</>}
        </span>
      </div>
      <p className="text-sm text-neutral-700">{r.detail}</p>
      {r.responded_at && (
        <div className="rounded-md bg-neutral-50 px-3 py-2 text-sm">
          <div className="text-xs text-neutral-500">
            Answered {formatDate(r.responded_at)}
          </div>
          {r.response_note && <p>{r.response_note}</p>}
          {r.response_document_id && (
            <p className="text-xs text-neutral-500">
              Document <Mono>{r.response_document_id}</Mono>
            </p>
          )}
        </div>
      )}
      {r.close_note && (
        <p className="text-xs text-neutral-500">
          Tex ({r.status}): {r.close_note}
        </p>
      )}
      <div className="text-xs text-neutral-400">
        Requested {formatDate(r.requested_at)}
      </div>
    </div>
  );
}

// ── Tex side: create, accept, cancel, reopen ──

function useIdempotencyKey() {
  const [key, setKey] = useState(() => crypto.randomUUID());
  return { key, rotate: () => setKey(crypto.randomUUID()) };
}

export function TexInformationRequestsCard({
  caseId,
  canWrite,
}: {
  caseId: string;
  canWrite: boolean;
}) {
  const requests = useInformationRequests(caseId);
  const [creating, setCreating] = useState(false);
  return (
    <Card
      title="Information requests"
      actions={
        canWrite ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setCreating((v) => !v)}
          >
            {creating ? "Cancel" : "New request"}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {creating && (
          <CreateRequestForm
            caseId={caseId}
            onDone={() => setCreating(false)}
          />
        )}
        {requests.isError && (
          <Alert tone="error">{errorMessage(requests.error)}</Alert>
        )}
        {requests.data && requests.data.requests.length === 0 && !creating && (
          <p className="text-sm text-neutral-500">
            Nothing asked of the borrower or dealer yet. Requests here are what
            stands between the memo and a package.
          </p>
        )}
        <ul className="divide-y divide-neutral-100">
          {requests.data?.requests.map((r) => (
            <li
              key={r.id}
              className="flex flex-col gap-2 py-3 md:flex-row md:items-start md:justify-between"
            >
              <RequestSummary r={r} showAudience />
              {canWrite && <TexRequestActions caseId={caseId} r={r} />}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

function CreateRequestForm({
  caseId,
  onDone,
}: {
  caseId: string;
  onDone: () => void;
}) {
  const { key, rotate } = useIdempotencyKey();
  const create = useCreateInformationRequest(caseId, key);
  const [audience, setAudience] =
    useState<InformationRequestAudience>("borrower");
  const [kind, setKind] = useState<InformationRequestKind>("clarification");
  const [documentType, setDocumentType] = useState("");
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [dueOn, setDueOn] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        audience,
        kind,
        document_type: kind === "document" ? documentType.trim() || null : null,
        title: title.trim(),
        detail: detail.trim(),
        due_on: dueOn || null,
      },
      {
        onSuccess: () => {
          rotate();
          onDone();
        },
      },
    );
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-3 rounded-md border border-[var(--border-subtle)] bg-neutral-50 p-3"
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label="Ask">
          <Select
            value={audience}
            onChange={(e) =>
              setAudience(e.target.value as InformationRequestAudience)
            }
            disabled={create.isPending}
          >
            <option value="borrower">the borrower</option>
            <option value="dealer">the dealer</option>
          </Select>
        </Field>
        <Field label="For">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as InformationRequestKind)}
            disabled={create.isPending}
          >
            <option value="clarification">a clarification</option>
            <option value="document">a document</option>
          </Select>
        </Field>
        {kind === "document" ? (
          <Field label="Document type" hint="snake_case, e.g. bank_statements">
            <Input
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              required
              pattern="^[a-z][a-z0-9_]{1,63}$"
              disabled={create.isPending}
            />
          </Field>
        ) : (
          <Field label="Due (optional)">
            <Input
              type="date"
              value={dueOn}
              onChange={(e) => setDueOn(e.target.value)}
              disabled={create.isPending}
            />
          </Field>
        )}
      </div>
      <Field label="Title">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          maxLength={200}
          disabled={create.isPending}
        />
      </Field>
      <Field
        label="What you need and why"
        hint="The addressee sees this text verbatim. Do not include the other party's private information."
      >
        <Textarea
          rows={3}
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          required
          maxLength={4000}
          disabled={create.isPending}
        />
      </Field>
      {kind === "document" && (
        <Field label="Due (optional)">
          <Input
            type="date"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
            disabled={create.isPending}
          />
        </Field>
      )}
      {create.isError && (
        <Alert tone="error">{errorMessage(create.error)}</Alert>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? "Sending…" : "Send request"}
        </Button>
      </div>
    </form>
  );
}

function TexRequestActions({
  caseId,
  r,
}: {
  caseId: string;
  r: InformationRequestView;
}) {
  const close = useCloseInformationRequest(caseId);
  const [pending, setPending] = useState<CloseCommand | null>(null);
  const [note, setNote] = useState("");
  if (r.status === "accepted" || r.status === "cancelled") return null;

  const run = (command: CloseCommand) => {
    close.mutate(
      { requestId: r.id, command, note: note.trim() || null },
      { onSuccess: () => setPending(null) },
    );
  };

  if (pending) {
    return (
      <form
        className="w-full space-y-2 md:w-72"
        onSubmit={(e) => {
          e.preventDefault();
          run(pending);
        }}
      >
        <Field
          label={
            pending === "reopen"
              ? "Why this does not answer it"
              : "Note (optional)"
          }
        >
          <Textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required={pending === "reopen"}
          />
        </Field>
        {close.isError && (
          <Alert tone="error">{errorMessage(close.error)}</Alert>
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setPending(null)}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant={pending === "cancel" ? "danger" : "primary"}
            disabled={close.isPending}
          >
            {humanize(pending)}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="flex shrink-0 gap-2">
      {r.status === "answered" && (
        <>
          <Button
            type="button"
            onClick={() => run("accept")}
            disabled={close.isPending}
          >
            Accept
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setPending("reopen")}
            disabled={close.isPending}
          >
            Send back
          </Button>
        </>
      )}
      <Button
        type="button"
        variant="danger"
        onClick={() => setPending("cancel")}
        disabled={close.isPending}
      >
        Cancel request
      </Button>
    </div>
  );
}

// ── addressee side: dealer or borrower answers what was asked of them ──

export function AddresseeInformationRequestsCard({
  caseId,
  audience,
}: {
  caseId: string;
  audience: InformationRequestAudience;
}) {
  const requests = useInformationRequests(caseId);
  const items = requests.data?.requests ?? [];
  if (requests.isPending || (items.length === 0 && !requests.isError))
    return null;
  const outstanding = items.filter((r) => r.status === "open");
  return (
    <Card
      title={
        audience === "borrower"
          ? "Tex has asked you for"
          : "Tex has asked the dealership for"
      }
    >
      <div className="space-y-4">
        {requests.isError && (
          <Alert tone="error">{errorMessage(requests.error)}</Alert>
        )}
        {outstanding.length === 0 && items.length > 0 && (
          <p className="text-sm text-neutral-500">
            Nothing outstanding. Earlier requests are listed below.
          </p>
        )}
        <ul className="divide-y divide-neutral-100">
          {items.map((r) => (
            <li key={r.id} className="space-y-2 py-3">
              <RequestSummary r={r} showAudience={false} />
              {r.status === "open" && <RespondForm caseId={caseId} r={r} />}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

function RespondForm({
  caseId,
  r,
}: {
  caseId: string;
  r: InformationRequestView;
}) {
  const { key, rotate } = useIdempotencyKey();
  const respond = useRespondToInformationRequest(caseId, key);
  const documents = useDocuments(caseId);
  const [note, setNote] = useState("");
  const [documentId, setDocumentId] = useState("");
  const usable = (documents.data?.documents ?? []).filter(
    (d) => d.availability === "available" && d.superseded_at === null,
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    respond.mutate(
      {
        requestId: r.id,
        note: note.trim() || null,
        document_id: documentId || null,
      },
      { onSuccess: rotate },
    );
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-2 rounded-md border border-[var(--border-subtle)] bg-neutral-50 p-3"
    >
      {r.kind === "document" && (
        <Field
          label="Attach a document you have uploaded to this request"
          hint="Upload it in Documents first if it is not listed; it must have finished scanning."
        >
          <Select
            value={documentId}
            onChange={(e) => setDocumentId(e.target.value)}
            required
            disabled={respond.isPending}
          >
            <option value="">Choose a document…</option>
            {usable.map((d) => (
              <option key={d.id} value={d.id}>
                {d.original_filename} · {humanize(d.document_type)}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Field label={r.kind === "document" ? "Note (optional)" : "Your answer"}>
        <Textarea
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          required={r.kind === "clarification"}
          maxLength={4000}
          disabled={respond.isPending}
        />
      </Field>
      {respond.isError && (
        <Alert tone="error">{errorMessage(respond.error)}</Alert>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={respond.isPending}>
          {respond.isPending ? "Sending…" : "Send to Tex"}
        </Button>
      </div>
    </form>
  );
}
